import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import http from "../lib/http";
import logoImage from "../assets/images/logo.png";
import "../styles/Recent.css";

export default function RecentTab() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  // NEW: filters
  const [range, setRange] = useState("all"); // today | 3d | 7d | all
  const [limit, setLimit] = useState(12);

  const fetchRecentlyViewed = async (nextRange = range, nextLimit = limit) => {
    try {
      setLoading(true);
      setError("");

      const res = await http.get("/api/customer/recently-viewed", {
        params: { range: nextRange, limit: nextLimit },
      });

      const list = Array.isArray(res?.data?.data) ? res.data.data : [];

      const normalized = list
        .map((row) => ({
          id: row?.id,
          viewed_at: row?.viewed_at,
          product: row?.product || null,
        }))
        .filter((x) => x.product && x.product.slug);

      setRecentlyViewed(normalized);
    } catch (e) {
      setError("Could not load recently viewed items.");
      setRecentlyViewed([]);
    } finally {
      setLoading(false);
    }
  };

  // Refetch when range/limit changes
  useEffect(() => {
    fetchRecentlyViewed(range, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, limit]);

  const recentGrid = useMemo(() => recentlyViewed.slice(0, 12), [recentlyViewed]);

  const getProductImage = (p) => {
    const arr = Array.isArray(p?.product_images) ? p.product_images : [];
    return arr[0] || logoImage;
  };

  const priceText = (p) => {
    const n = Number(p?.discount_total_amount ?? p?.price ?? 0);
    return `$${n.toFixed(2)}`;
  };

  const formatViewedAt = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
  };

  return (
    <div>
      <div className="acc-head">
        <h1 className="acc-h1">Recently Viewed</h1>

        <div className="recent-actions">
          {/* NEW: Range Filter */}
          <select
            className="acc-select"
            value={range}
            onChange={(e) => setRange(e.target.value)}
            disabled={loading}
          >
            <option value="all">All</option>
            <option value="today">Today</option>
            <option value="3d">Last 3 days</option>
            <option value="7d">Last 7 days</option>
          </select>

          {/* NEW: Limit */}
          <select
            className="acc-select"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            disabled={loading}
          >
            <option value={12}>12</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>

          <button
            className="acc-btn"
            onClick={() => fetchRecentlyViewed(range, limit)}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="acc-box">Loading recently viewed...</div>
      ) : error ? (
        <div className="acc-error">{error}</div>
      ) : recentlyViewed.length === 0 ? (
        <div className="acc-box">
          No recently viewed products.
          <div style={{ marginTop: 12 }}>
            <button className="acc-btn" onClick={() => navigate("/explore")}>
              Browse Products
            </button>
          </div>
        </div>
      ) : (
        <div className="recent-grid">
          {recentGrid.map((row) => {
            const p = row.product;
            return (
              <div
                key={row.id}
                className="recent-card"
                onClick={() => navigate(`/product/${p.slug}`)}
                role="button"
                tabIndex={0}
              >
                <div className="recent-card-img">
                  <img
                    src={getProductImage(p)}
                    alt={p?.name}
                    onError={(e) => (e.target.src = logoImage)}
                  />
                </div>
                <div className="recent-card-body">
                  <div className="recent-card-name">{p?.name}</div>
                  <div className="recent-card-footer">
                    <div className="recent-price">{priceText(p)}</div>
                    <div className="recent-viewed-at">{formatViewedAt(row.viewed_at)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}